import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {REPORTS, REPORT_GROUPS, ReportDef, ReportGroup} from './reportCatalogue';
import './reports.css';

/**
 * Reports landing screen.
 *
 * This is the navigation for the whole module — the sidebar carries a single
 * "Reports" entry rather than fourteen, because the value of this screen is the
 * "what it answers" line, which a sidebar cannot show.
 *
 * Each report is a card (an ordinary link, so it works with the keyboard, middle
 * click and "open in new tab"), grouped by the question it answers. Chips above
 * the cards narrow the page to one group. There is deliberately no text search:
 * with this few reports the groups and cards are quicker than typing.
 *
 * The wireframe's "Rows on stage" and "Audience" columns are intentionally
 * absent: both were specification scaffolding. Row counts expose internal data
 * volumes and mean nothing to a user, and audience always reads the same value
 * for whoever is looking, since a user only ever sees the reports their role
 * permits.
 */

/** Accent colour per group, used for the card's top border and icon badge. */
const GROUP_ACCENT: Record<ReportGroup, string> = {
  money: '#2E7D5B',
  growth: '#3B6FB6',
  supply: '#C9873A',
  printing: '#7B5EA7'
};

/** Font Awesome icon per report code; anything new falls back to a chart. */
const REPORT_ICON: Record<string, string> = {
  REV1: 'fa-pound-sign',
  REV2: 'fa-user-graduate',
  REV3: 'fa-file-invoice',
  REV4: 'fa-receipt',
  LEAD1: 'fa-filter',
  LEAD2: 'fa-bullseye',
  LEAD3: 'fa-tachometer-alt',
  ORD1: 'fa-truck',
  ORD2: 'fa-boxes',
  ORD3: 'fa-clipboard-list',
  PRT1: 'fa-print',
  PRT2: 'fa-bolt',
  PRT3: 'fa-exclamation-triangle'
};

type GroupFilter = 'all' | ReportGroup;

const ReportsIndex = () => {
  const [activeGroup, setActiveGroup] = useState<GroupFilter>('all');

  const inGroup = (group: ReportGroup): ReportDef[] =>
    REPORTS.filter((r) => r.group === group);

  const visibleGroups = REPORT_GROUPS.filter(
    (g) => (activeGroup === 'all' || activeGroup === g.key) && inGroup(g.key).length > 0
  );

  const accentStyle = (group: ReportGroup) =>
    ({
      '--accent': GROUP_ACCENT[group],
      '--accent-soft': `${GROUP_ACCENT[group]}1A`
    } as React.CSSProperties);

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Reports</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <p className="text-muted">
                    {REPORTS.length} reports across {REPORT_GROUPS.length} areas.
                    Open one to filter the data, view the charts and export to
                    Excel.
                  </p>
                  <div role="group" aria-label="Filter by group">
                    <button
                      type="button"
                      aria-pressed={activeGroup === 'all'}
                      className={`report-chip${activeGroup === 'all' ? ' report-chip--active' : ''}`}
                      onClick={() => setActiveGroup('all')}>
                      All
                      <span className="report-chip__count">{REPORTS.length}</span>
                    </button>
                    {REPORT_GROUPS.map((g) => (
                      <button
                        type="button"
                        key={g.key}
                        aria-pressed={activeGroup === g.key}
                        className={`report-chip${activeGroup === g.key ? ' report-chip--active' : ''}`}
                        onClick={() => setActiveGroup(g.key)}>
                        {g.label}
                        <span className="report-chip__count">{inGroup(g.key).length}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {visibleGroups.map((group) => {
            const rows = inGroup(group.key);

            return (
              <div key={group.key} className="mb-2">
                <div className="d-flex align-items-baseline flex-wrap mb-2">
                  <h2 className="h5 mb-0 mr-2">{group.label}</h2>
                  <span className="text-muted d-none d-md-inline">{group.blurb}</span>
                </div>
                <div className="row">
                  {rows.map((report) => (
                    <div className="col-xl-3 col-lg-4 col-md-6 mb-3" key={report.code}>
                      <Link
                        to={'/reports/' + report.slug}
                        className="report-card"
                        style={accentStyle(report.group)}
                        aria-label={'Open ' + report.name}>
                        <div className="d-flex align-items-center mb-3">
                          <span className="report-card__icon" aria-hidden="true">
                            <i className={`fas ${REPORT_ICON[report.code] || 'fa-chart-bar'}`} />
                          </span>
                          <strong className="ml-3">{report.name}</strong>
                        </div>
                        <p className="report-card__question text-muted mb-3">{report.question}</p>
                        <span className="report-card__open">
                          Open report <i className="fas fa-arrow-right ml-1" aria-hidden="true" />
                        </span>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ReportsIndex;
